<script setup lang="ts">
/**
 * SectionMultiSplit
 *
 * Zig-zag storytelling component that auto-alternates image/text layout.
 * Perfect for breaking down feature lists into digestible visual blocks.
 *
 * Interaction: "Look at this" (storytelling content)
 * - Image: Subtle zoom on hover
 * - Points: Background tint on hover for focus
 */

interface PointItem {
  icon?: string;
  title: string;
  description?: string;
}

interface StoryBlock {
  title: string;
  description?: string;
  points: PointItem[];
  image: {
    src: string;
    alt?: string;
  };
}

interface SectionMultiSplitProps {
  sectionTitle?: string;
  sectionSubtitle?: string;
  blocks?: StoryBlock[];
  sectionBg?: "white" | "neutral";
  imageStyle?: "default" | "styled" | "accent-box";
}

const props = withDefaults(defineProps<SectionMultiSplitProps>(), {
  sectionTitle: "",
  sectionSubtitle: "",
  blocks: () => [],
  sectionBg: "white",
  imageStyle: "styled",
});

const bgClass = computed(() =>
  props.sectionBg === "neutral" ? "bg-neutral-50" : "bg-white"
);

const imageContainerClass = computed(() => {
  const base = "relative overflow-hidden rounded-2xl";
  switch (props.imageStyle) {
    case "accent-box":
    case "styled":
      return `${base} shadow-xl shadow-slate-200/50`;
    case "default":
    default:
      return base;
  }
});

/**
 * Determine if image should be on left (even index) or right (odd index)
 */
const isImageLeft = (index: number): boolean => index % 2 === 0;

/**
 * Convert icon name to Nuxt Icon format
 * Handles: "Shield" -> "lucide:shield", "ShieldCheck" -> "lucide:shield-check"
 * Also handles already-formatted names like "lucide:shield"
 */
const formatIconName = (iconName?: string): string => {
  if (!iconName) return "lucide:check";
  if (iconName.includes(":")) return iconName;

  // Convert PascalCase to kebab-case
  const kebab = iconName.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();

  return `lucide:${kebab}`;
};
</script>

<template>
  <section :class="[bgClass, 'py-16 sm:py-24']">
    <div class="mx-auto max-w-7xl px-8">
      <div v-if="sectionTitle" class="mb-16 text-center">
        <h2 class="text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
          {{ sectionTitle }}
        </h2>
        <p v-if="sectionSubtitle" class="mx-auto mt-4 max-w-2xl text-lg text-neutral-600">
          {{ sectionSubtitle }}
        </p>
      </div>

      <div class="space-y-16 lg:space-y-24">
        <div
          v-for="(block, index) in blocks"
          :key="index"
          class="grid items-center gap-12 lg:grid-cols-2 lg:gap-16"
        >
          <!-- Image Column -->
          <div :class="{ 'lg:order-2': !isImageLeft(index) }">
            <div v-if="imageStyle === 'accent-box'" class="relative">
              <div
                class="absolute -bottom-3 -right-3 size-full rounded-2xl bg-mw-accent-400/20"
                :class="{ '-left-3 -right-auto': !isImageLeft(index) }"
                aria-hidden="true"
              ></div>
              <div :class="imageContainerClass" class="group/image relative">
                <ResponsiveImage
                  :src="block.image.src"
                  :alt="block.image.alt || block.title"
                  class="size-full object-cover transition-transform duration-700 group-hover/image:scale-105"
                  loading="eager"
                  :width="1200"
                  :height="675"
                />
              </div>
            </div>

            <div v-else :class="imageContainerClass" class="group/image">
              <ResponsiveImage
                :src="block.image.src"
                :alt="block.image.alt || block.title"
                class="size-full object-cover transition-transform duration-700 group-hover/image:scale-105"
                loading="eager"
                :width="1200"
                :height="675"
              />
            </div>
          </div>

          <!-- Content Column -->
          <div :class="{ 'lg:order-1': !isImageLeft(index) }">
            <h3
              class="mb-4 text-xl font-extrabold tracking-tight text-slate-700 sm:text-2xl"
            >
              {{ block.title }}
            </h3>

            <p v-if="block.description" class="mb-6 text-lg text-neutral-600">
              {{ block.description }}
            </p>

            <!-- Points List with hover backgrounds -->
            <div class="space-y-2">
              <div
                v-for="(point, pointIndex) in block.points"
                :key="pointIndex"
                class="-mx-3 flex gap-4 rounded-lg p-3 transition-colors duration-200 hover:bg-neutral-50"
              >
                <div
                  class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-mw-accent-50"
                >
                  <Icon
                    :name="formatIconName(point.icon)"
                    size="24"
                    class="text-mw-accent-500"
                  />
                </div>
                <div>
                  <h4 class="text-lg font-bold text-slate-900">
                    {{ point.title }}
                  </h4>
                  <p v-if="point.description" class="mt-1 text-neutral-600">
                    {{ point.description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>
